/**
 * Catálogo dinámico de transiciones tipo gl-transitions (MIT).
 *
 * Cada entrada incluye el cuerpo GLSL ES 3.0 con la función:
 *   `vec4 transition(vec2 uv)`
 * y opcionalmente helpers. Disponibles uniforms:
 *   `sampler2D from`, `sampler2D to`, `float progress`
 * y funciones:
 *   `getFromColor(uv)`, `getToColor(uv)`, `rand(vec2)`
 *
 * Los shaders se compilan bajo demanda (la primera vez que se usan) y
 * el `WebGLProgram` se cachea en el compositor.
 */

export interface DynamicTransition {
  id: string;
  label: string;
  category: string;
  icon?: string;
  /** Cuerpo GLSL: declarar `vec4 transition(vec2 uv) { ... }` */
  glsl: string;
}

export const DYNAMIC_TRANSITIONS: DynamicTransition[] = [
  // ===== Movimiento =====================================================
  {
    id: "bounce",
    label: "Rebote",
    category: "Movimiento",
    icon: "⤴",
    glsl: `
      const float PI = 3.14159265358;
      vec4 transition(vec2 uv) {
        float time = progress;
        float stime = sin(time * PI / 2.0);
        float phase = time * PI * 3.0;
        float y = abs(cos(phase)) * (1.0 - stime);
        float d = uv.y - y;
        return mix(
          mix(getToColor(uv), vec4(0.0, 0.0, 0.0, 0.6),
              step(d, 0.075) * (1.0 - mix((d/0.075)*0.6 + 0.4, 1.0, smoothstep(0.95, 1.0, progress)))),
          getFromColor(vec2(uv.x, uv.y + (1.0 - y))),
          step(d, 0.0)
        );
      }
    `,
  },
  {
    id: "swap3d",
    label: "Intercambio 3D",
    category: "Movimiento",
    icon: "⇄",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 p = uv;
        float t = progress;
        float perspective = 1.0 - t * 0.7;
        if (t < 0.5) {
          // A se aleja
          vec2 q = (p - 0.5) / perspective + 0.5;
          if (q.x < 0.0 || q.x > 1.0 || q.y < 0.0 || q.y > 1.0)
            return vec4(0,0,0,1);
          return getFromColor(q);
        } else {
          float t2 = (t - 0.5) * 2.0;
          vec2 q = (p - 0.5) / (0.3 + t2 * 0.7) + 0.5;
          if (q.x < 0.0 || q.x > 1.0 || q.y < 0.0 || q.y > 1.0)
            return vec4(0,0,0,1);
          return getToColor(q);
        }
      }
    `,
  },
  {
    id: "directionalWarp",
    label: "Warp direccional",
    category: "Movimiento",
    icon: "↘",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 dir = vec2(-1.0, 1.0);
        vec2 v = normalize(dir);
        v /= abs(v.x) + abs(v.y);
        float d = v.x * 0.5 + v.y * 0.5;
        float m = 1.0 - smoothstep(-0.1, 0.0, v.x * uv.x + v.y * uv.y - (d - 0.5 + progress * (1.0 + 0.1)));
        return mix(getFromColor((uv - 0.5) * (1.0 - m) + 0.5),
                   getToColor((uv - 0.5) * m + 0.5), m);
      }
    `,
  },
  {
    id: "cubeflip",
    label: "Cube flip",
    category: "Movimiento",
    icon: "▣",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 p = uv;
        float persp = 0.7;
        float unzoom = 0.3;
        float reflection = 0.4;
        float floating = 3.0;
        vec2 fromP = (p - 0.5) * (1.0 - progress * unzoom) + 0.5;
        vec2 toP = (p - 0.5) * (1.0 - (1.0-progress) * unzoom) + 0.5;
        float persp1 = 1.0 - progress * persp;
        fromP.y = (fromP.y - 0.5) / persp1 + 0.5;
        float persp2 = 1.0 - (1.0-progress) * persp;
        toP.y = (toP.y - 0.5) / persp2 + 0.5;
        if (progress < 0.5) {
          if (fromP.x < 0.0 || fromP.x > 1.0 || fromP.y < 0.0 || fromP.y > 1.0)
            return vec4(0,0,0,1);
          return getFromColor(fromP);
        }
        if (toP.x < 0.0 || toP.x > 1.0 || toP.y < 0.0 || toP.y > 1.0)
          return vec4(0,0,0,1);
        return getToColor(toP);
      }
    `,
  },

  // ===== Distorsión / Físicas ==========================================
  {
    id: "crosshatchDetail",
    label: "Cruces detalladas",
    category: "Patrones",
    icon: "✕",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 c = uv - 0.5;
        float d = max(0.0, 0.04 + abs(c.x) + abs(c.y));
        float r = rand(uv) - 0.5;
        float s = 0.03 * progress;
        c += vec2(cos(uv.y * 50.0) * s, sin(uv.x * 50.0) * s);
        float threshold = pow(progress, 2.0) * 8.0;
        return mix(getFromColor(uv + c * r), getToColor(uv + c * r),
                   step(d, threshold));
      }
    `,
  },
  {
    id: "perlin",
    label: "Perlin",
    category: "Patrones",
    icon: "≋",
    glsl: `
      float perlinHash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
      }
      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        return mix(mix(perlinHash(i), perlinHash(i + vec2(1.0,0.0)), f.x),
                   mix(perlinHash(i + vec2(0.0,1.0)), perlinHash(i + vec2(1.0,1.0)), f.x), f.y);
      }
      vec4 transition(vec2 uv) {
        float n = noise(uv * 8.0);
        float mask = step(progress * 1.4 - 0.2, n + uv.x * 0.001);
        return mix(getToColor(uv), getFromColor(uv), mask);
      }
    `,
  },
  {
    id: "windowSlice",
    label: "Slice ventana",
    category: "Patrones",
    icon: "⫶",
    glsl: `
      vec4 transition(vec2 uv) {
        float pr = smoothstep(-0.4, 0.0, uv.x - progress * 1.4);
        float s = step(pr, fract(12.0 * uv.x));
        return mix(getFromColor(uv), getToColor(uv), s);
      }
    `,
  },
  {
    id: "stripsWipe",
    label: "Tiras alternas",
    category: "Patrones",
    icon: "≣",
    glsl: `
      vec4 transition(vec2 uv) {
        float stripeIdx = floor(uv.y * 10.0);
        bool even = mod(stripeIdx, 2.0) < 1.0;
        float p = even ? uv.x : 1.0 - uv.x;
        return p < progress ? getToColor(uv) : getFromColor(uv);
      }
    `,
  },
  {
    id: "randomSquares",
    label: "Cuadrados aleatorios",
    category: "Patrones",
    icon: "▢",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 g = floor(uv * 12.0);
        float r = rand(g);
        return r < progress * 1.3 ? getToColor(uv) : getFromColor(uv);
      }
    `,
  },

  // ===== Formas =========================================================
  {
    id: "circleShape",
    label: "Círculo cinemático",
    category: "Formas",
    icon: "◯",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 c = (uv - 0.5) * vec2(1.0, 1.0);
        float d = length(c);
        float r = progress * 0.72;
        float edge = smoothstep(r - 0.02, r + 0.02, d);
        return mix(getToColor(uv), getFromColor(uv), edge);
      }
    `,
  },
  {
    id: "hexagonalize",
    label: "Hexagonalizar",
    category: "Formas",
    icon: "⬢",
    glsl: `
      vec2 hexUv(vec2 p, float s) {
        vec2 q = vec2(p.x * 1.1547, p.y + p.x * 0.5);
        vec2 pi = floor(q * s);
        vec3 v = vec3(pi.xy, 1.0 - pi.x - pi.y);
        v -= floor(v);
        return v.xy;
      }
      vec4 transition(vec2 uv) {
        float steps = 50.0;
        float dist = 2.0 * min(progress, 1.0 - progress);
        float s = mix(1.0, 1.0 + 8.0 * dist, dist);
        vec2 h = hexUv(uv, s);
        vec2 dx = vec2(0.0, length(h - 0.5)) * 0.0;
        return mix(getFromColor(uv + dx), getToColor(uv + dx), progress);
      }
    `,
  },
  {
    id: "circleCrop",
    label: "Recorte circular",
    category: "Formas",
    icon: "◐",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 c = uv - 0.5;
        float r = length(c);
        float radius;
        if (progress < 0.5) radius = 0.71 - progress * 1.42;
        else radius = (progress - 0.5) * 1.42;
        if (r > radius) return progress < 0.5 ? getFromColor(uv) : getToColor(uv);
        return vec4(0.0, 0.0, 0.0, 1.0);
      }
    `,
  },
  {
    id: "polkadotCurtain",
    label: "Cortina de lunares",
    category: "Formas",
    icon: "●",
    glsl: `
      vec4 transition(vec2 uv) {
        float dots = 20.0;
        vec2 center = vec2(0.0);
        bool nextImage = distance(fract(uv * dots), vec2(0.5)) < progress / distance(uv, center);
        return nextImage ? getToColor(uv) : getFromColor(uv);
      }
    `,
  },

  // ===== Pliegues / 3D =================================================
  {
    id: "doorwayClassic",
    label: "Puerta cinemática",
    category: "Pliegues / 3D",
    icon: "⊟",
    glsl: `
      vec4 transition(vec2 uv) {
        float reflectionR = 0.4;
        float perspective = 0.4;
        float depth = 3.0;
        vec2 pFr = vec2(uv.x / (1.0 - progress), uv.y);
        vec2 pTo = vec2((uv.x - 0.5*progress)/(1.0 - progress) + 0.0, uv.y);
        if (uv.x < 1.0 - progress) {
          return getFromColor(pFr * vec2(1.0, 1.0));
        }
        return getToColor(uv);
      }
    `,
  },
  {
    id: "pageCurlPro",
    label: "Pasar página pro",
    category: "Pliegues / 3D",
    icon: "📄",
    glsl: `
      vec4 transition(vec2 uv) {
        float a = uv.x + uv.y - 0.5;
        if (a < progress * 2.0) return getToColor(uv);
        float edge = smoothstep(progress * 2.0, progress * 2.0 + 0.04, a);
        float shadow = smoothstep(progress * 2.0, progress * 2.0 + 0.15, a);
        vec3 c = getFromColor(uv).rgb * mix(0.4, 1.0, shadow);
        // brillo en el borde
        c = mix(vec3(1.0), c, edge);
        return vec4(c, 1.0);
      }
    `,
  },
  {
    id: "fold",
    label: "Pliegue libro",
    category: "Pliegues / 3D",
    icon: "📖",
    glsl: `
      vec4 transition(vec2 uv) {
        if (uv.x < 0.5) {
          vec2 q = vec2(uv.x / (0.5 - progress * 0.5), uv.y);
          if (q.x > 1.0) return getToColor(uv);
          return getFromColor(q * vec2(0.5, 1.0));
        } else {
          vec2 q = vec2((uv.x - 0.5) / (0.5 - progress * 0.5) + 0.5, uv.y);
          if (q.x < 0.5) return getToColor(uv);
          return getFromColor(q);
        }
      }
    `,
  },

  // ===== Distorsión cinemática =========================================
  {
    id: "luminance",
    label: "Por luminancia",
    category: "Cinemáticas",
    icon: "◐",
    glsl: `
      vec4 transition(vec2 uv) {
        vec4 fr = getFromColor(uv);
        vec4 to = getToColor(uv);
        float lum = dot(fr.rgb, vec3(0.299, 0.587, 0.114));
        float threshold = progress * 1.2 - 0.1;
        float mask = smoothstep(threshold - 0.05, threshold + 0.05, lum);
        return mix(to, fr, mask);
      }
    `,
  },
  {
    id: "morphFlow",
    label: "Morfear flujo",
    category: "Cinemáticas",
    icon: "≈",
    glsl: `
      vec4 transition(vec2 uv) {
        vec4 a = getFromColor(uv);
        vec4 b = getToColor(uv);
        float strength = 0.1;
        vec2 disp = (a.rg - b.rg) * strength * sin(progress * 3.14159);
        return mix(getFromColor(uv + disp), getToColor(uv - disp), progress);
      }
    `,
  },
  {
    id: "dreamy",
    label: "Soñador",
    category: "Cinemáticas",
    icon: "✨",
    glsl: `
      vec2 offsetTr(float p, vec2 uv) {
        float x = sin(p * 3.14159) * 0.03;
        float y = cos(p * 3.14159) * 0.03;
        return uv + vec2(x, y);
      }
      vec4 transition(vec2 uv) {
        return mix(getFromColor(offsetTr(progress, uv)), getToColor(offsetTr(1.0 - progress, uv)), progress);
      }
    `,
  },
  {
    id: "kaleidoSwirl",
    label: "Caleidoscopio swirl",
    category: "Cinemáticas",
    icon: "✻",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 c = uv - 0.5;
        float r = length(c);
        float ang = atan(c.y, c.x);
        ang += progress * 6.283 * 0.5;
        float seg = 6.283 / 8.0;
        ang = mod(ang, seg);
        ang = abs(ang - seg * 0.5);
        vec2 q = vec2(cos(ang), sin(ang)) * r + 0.5;
        return mix(getFromColor(q), getToColor(uv), progress);
      }
    `,
  },
  {
    id: "rotateInOut",
    label: "Rotación entrada",
    category: "Cinemáticas",
    icon: "↻",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 c = uv - 0.5;
        float ang = progress * 3.14159 * 2.0;
        mat2 R = mat2(cos(ang), -sin(ang), sin(ang), cos(ang));
        float s = 1.0 / (1.0 + 1.5 * progress);
        vec2 a = R * c * s + 0.5;
        if (a.x < 0.0 || a.x > 1.0 || a.y < 0.0 || a.y > 1.0)
          return getToColor(uv);
        return mix(getFromColor(a), getToColor(uv), smoothstep(0.6, 1.0, progress));
      }
    `,
  },

  // ===== Glitch / FX TV ================================================
  {
    id: "glitchDisplacement",
    label: "Glitch desplazado",
    category: "Glitch / FX TV",
    icon: "▓",
    glsl: `
      vec4 transition(vec2 uv) {
        float h = floor(uv.y * 30.0);
        float r = rand(vec2(h, floor(progress * 8.0)));
        vec2 off = vec2((r - 0.5) * sin(progress * 3.14159) * 0.4, 0.0);
        vec3 a = getFromColor(uv + off).rgb;
        vec3 b = getToColor(uv - off).rgb;
        vec3 c = mix(a, b, progress);
        c.r = mix(a.r, b.r, clamp(progress - 0.18, 0.0, 1.0));
        c.b = mix(a.b, b.b, clamp(progress + 0.18, 0.0, 1.0));
        return vec4(c, 1.0);
      }
    `,
  },
  {
    id: "glitchMemories",
    label: "Glitch recuerdos",
    category: "Glitch / FX TV",
    icon: "📺",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 block = floor(uv * vec2(16.0));
        float r = rand(block + floor(progress * 16.0));
        vec2 jit = (r > 0.5 ? vec2(0.08, 0.0) : vec2(-0.08, 0.0)) * sin(progress * 3.14);
        vec3 a = getFromColor(uv + jit).rgb;
        vec3 b = getToColor(uv).rgb;
        return vec4(mix(a, b, progress), 1.0);
      }
    `,
  },
  {
    id: "screenBurst",
    label: "Explosión de pantalla",
    category: "Glitch / FX TV",
    icon: "💥",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 c = uv - 0.5;
        float r = length(c);
        float burst = step(r, progress * 0.8) * (1.0 - step(r, progress * 0.7));
        vec3 white = vec3(1.0);
        vec3 col = mix(getFromColor(uv).rgb, getToColor(uv).rgb, progress);
        return vec4(mix(col, white, burst), 1.0);
      }
    `,
  },
  {
    id: "rgbShift",
    label: "RGB shift extremo",
    category: "Glitch / FX TV",
    icon: "🟥",
    glsl: `
      vec4 transition(vec2 uv) {
        float s = sin(progress * 3.14159) * 0.08;
        vec3 c;
        c.r = mix(getFromColor(uv + vec2(s, 0)).r,  getToColor(uv + vec2(s, 0)).r,  progress);
        c.g = mix(getFromColor(uv).g,                getToColor(uv).g,                progress);
        c.b = mix(getFromColor(uv - vec2(s, 0)).b,  getToColor(uv - vec2(s, 0)).b,  progress);
        return vec4(c, 1.0);
      }
    `,
  },

  // ===== Líquidos / Naturaleza =========================================
  {
    id: "waterDropDeep",
    label: "Gota profunda",
    category: "Líquidos",
    icon: "💧",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 c = uv - 0.5;
        float r = length(c);
        float amp = 0.04 * (1.0 - progress);
        float wave = sin(r * 40.0 - progress * 20.0);
        vec2 q = uv + normalize(c) * wave * amp;
        return mix(getFromColor(q), getToColor(uv), smoothstep(progress - 0.2, progress + 0.1, r * 1.4));
      }
    `,
  },
  {
    id: "fireBurn",
    label: "Fuego intenso",
    category: "Líquidos",
    icon: "🔥",
    glsl: `
      vec4 transition(vec2 uv) {
        vec3 fire = vec3(1.0, 0.45, 0.1);
        float n1 = rand(uv * (40.0 + sin(progress*15.0) * 30.0));
        float n2 = rand(uv * 90.0 + progress * 7.0);
        float edge = smoothstep(progress - 0.18, progress + 0.06, n1);
        float burn = smoothstep(progress - 0.08, progress + 0.10, n2);
        vec3 a = getFromColor(uv).rgb;
        vec3 b = getToColor(uv).rgb;
        vec3 col = mix(fire * burn, mix(a, b, progress), edge);
        return vec4(col, 1.0);
      }
    `,
  },
  {
    id: "smokeFade",
    label: "Niebla",
    category: "Líquidos",
    icon: "☁",
    glsl: `
      vec4 transition(vec2 uv) {
        float n = rand(uv * 20.0 + progress * 5.0);
        float disp = sin(uv.y * 30.0 + progress * 8.0) * 0.02 * sin(progress * 3.14);
        vec2 q = uv + vec2(disp, 0.0);
        vec3 a = getFromColor(q).rgb;
        vec3 b = getToColor(q).rgb;
        return vec4(mix(a, b, smoothstep(0.0, 1.0, progress + n * 0.1 - 0.05)), 1.0);
      }
    `,
  },

  // ===== Light / Optical ===============================================
  {
    id: "lensFlare",
    label: "Destello (lens flare)",
    category: "Luz",
    icon: "⊛",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 c = uv - 0.5;
        float r = length(c);
        float flare = smoothstep(0.45, 0.5, progress) * (1.0 - smoothstep(0.5, 0.55, progress));
        vec3 col = mix(getFromColor(uv).rgb, getToColor(uv).rgb, progress);
        col += (1.0 - r) * flare * 1.5 * vec3(1.0, 0.95, 0.7);
        return vec4(col, 1.0);
      }
    `,
  },
  {
    id: "whiteFlash",
    label: "Flash blanco",
    category: "Luz",
    icon: "✦",
    glsl: `
      vec4 transition(vec2 uv) {
        float flash = sin(progress * 3.14159) * 1.4;
        vec3 col = mix(getFromColor(uv).rgb, getToColor(uv).rgb, progress);
        return vec4(col + flash, 1.0);
      }
    `,
  },
  {
    id: "godRays",
    label: "Rayos de luz",
    category: "Luz",
    icon: "☀",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 c = uv - 0.5;
        float ang = atan(c.y, c.x);
        float ray = 0.5 + 0.5 * sin(ang * 8.0 + progress * 4.0);
        float r = length(c);
        float fadeR = smoothstep(0.7, 0.2, r);
        vec3 a = getFromColor(uv).rgb;
        vec3 b = getToColor(uv).rgb;
        vec3 col = mix(a, b, progress);
        col += ray * fadeR * (1.0 - abs(progress - 0.5) * 2.0) * 0.4;
        return vec4(col, 1.0);
      }
    `,
  },

  // ===== Píxeles / Digital =============================================
  {
    id: "pixelizePro",
    label: "Pixelar pro",
    category: "Píxel / Digital",
    icon: "▦",
    glsl: `
      vec4 transition(vec2 uv) {
        float blocks = mix(40.0, 4.0, sin(progress * 3.14159));
        vec2 g = floor(uv * blocks) / blocks + 0.5 / blocks;
        return mix(getFromColor(g), getToColor(g), progress);
      }
    `,
  },
  {
    id: "ascii",
    label: "Pixel ASCII",
    category: "Píxel / Digital",
    icon: "▤",
    glsl: `
      vec4 transition(vec2 uv) {
        float blocks = 80.0;
        vec2 g = floor(uv * blocks) / blocks;
        float n = rand(g);
        float lum = dot(getFromColor(g).rgb, vec3(0.3, 0.6, 0.1));
        float showB = step(progress * 1.1, n + lum * 0.2);
        return showB > 0.5 ? getFromColor(g) : getToColor(uv);
      }
    `,
  },
  {
    id: "binaryRain",
    label: "Lluvia binaria",
    category: "Píxel / Digital",
    icon: "⠿",
    glsl: `
      vec4 transition(vec2 uv) {
        float cols = 30.0;
        float col = floor(uv.x * cols);
        float dropPos = mod(progress * 2.0 + rand(vec2(col, 0.0)) * 1.5, 2.0);
        float dist = abs(uv.y - dropPos);
        float drop = smoothstep(0.1, 0.0, dist);
        vec3 green = vec3(0.0, 1.0, 0.4);
        vec3 base = mix(getFromColor(uv).rgb, getToColor(uv).rgb, progress);
        return vec4(base + drop * green * (1.0 - progress), 1.0);
      }
    `,
  },

  // ===== Geometría =====================================================
  {
    id: "trianglesFlip",
    label: "Triángulos",
    category: "Geometría",
    icon: "▲",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 g = uv * 12.0;
        vec2 ipos = floor(g);
        vec2 fpos = fract(g);
        bool upper = (fpos.x + fpos.y) > 1.0;
        float idx = ipos.x + ipos.y * 12.0 + (upper ? 0.5 : 0.0);
        float r = rand(vec2(idx, 0.0));
        return r < progress * 1.3 ? getToColor(uv) : getFromColor(uv);
      }
    `,
  },
  {
    id: "ringWipe",
    label: "Anillo expandente",
    category: "Geometría",
    icon: "◯",
    glsl: `
      vec4 transition(vec2 uv) {
        float d = distance(uv, vec2(0.5));
        float ring = abs(d - progress * 0.71) < 0.04 ? 1.0 : 0.0;
        vec3 base = d < progress * 0.71 ? getToColor(uv).rgb : getFromColor(uv).rgb;
        return vec4(base + ring * vec3(1.0), 1.0);
      }
    `,
  },
  {
    id: "spiralWipe",
    label: "Espiral",
    category: "Geometría",
    icon: "🌀",
    glsl: `
      vec4 transition(vec2 uv) {
        vec2 c = uv - 0.5;
        float ang = (atan(c.y, c.x) + 3.14159) / 6.283;
        float r = length(c) * 2.0;
        float spiral = mod(ang - r * 2.0 + progress * 2.0, 1.0);
        return spiral < progress ? getToColor(uv) : getFromColor(uv);
      }
    `,
  },
  {
    id: "checkerboard",
    label: "Tablero de ajedrez",
    category: "Geometría",
    icon: "▦",
    glsl: `
      vec4 transition(vec2 uv) {
        float n = 8.0;
        vec2 g = floor(uv * n);
        bool even = mod(g.x + g.y, 2.0) < 1.0;
        float r = rand(g);
        float delay = even ? 0.0 : 0.5;
        float local = clamp((progress * 1.6 - delay - r * 0.2), 0.0, 1.0);
        return local > 0.5 ? getToColor(uv) : getFromColor(uv);
      }
    `,
  },

  // ===== Color =========================================================
  {
    id: "colorBlend",
    label: "Mezcla por color",
    category: "Color",
    icon: "🎨",
    glsl: `
      vec4 transition(vec2 uv) {
        vec3 a = getFromColor(uv).rgb;
        vec3 b = getToColor(uv).rgb;
        // Tinte intermedio
        vec3 tint = vec3(0.9, 0.2, 0.5);
        vec3 mid = mix(a, tint, sin(progress * 3.14159) * 0.7);
        return vec4(mix(mid, b, progress), 1.0);
      }
    `,
  },
  {
    id: "invertSwap",
    label: "Inversión",
    category: "Color",
    icon: "◐",
    glsl: `
      vec4 transition(vec2 uv) {
        vec3 a = getFromColor(uv).rgb;
        vec3 b = getToColor(uv).rgb;
        float flash = sin(progress * 3.14159);
        vec3 inv = mix(a, 1.0 - a, smoothstep(0.0, 0.5, progress));
        inv = mix(inv, 1.0 - b, smoothstep(0.5, 1.0, progress));
        inv = mix(inv, b, smoothstep(0.75, 1.0, progress));
        return vec4(inv, 1.0);
      }
    `,
  },
];
